<template>
  <div>
    <app-input
      v-if="showAdminRoleSelector"
      v-model="adminRole"
      type="select"
      name="Admin role"
      label="Admin Role"
      :options="adminRoleOptions"
      description="Recognition only admins can manage recognition programs and funding, and nothing else."
    ></app-input>

    <!-- Tesco Admin Form (when tescoPortalStubsEnabled) -->
    <template v-if="tescoPortalStubsEnabled">
      <ValidationObserver v-slot="v">
        <b-form @submit.prevent="v.handleSubmit(onSave)">
          <!-- Standard User Fields -->
          <app-input
            v-model="admin.username"
            v-if="!shouldHideUsername"
            :rules="{ required: true, regex: /[a-zA-Z][a-zA-Z0-9_]*$/ }"
            :extraState="!errorDuplicated.username"
            type="text"
            name="username"
            label="Username"
            @input="onDuplicateInput('username')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.username"
              >Username is taken, please choose another one</b-form-invalid-feedback
            >
          </app-input>

          <app-input
            v-model="admin.email"
            rules="required|email"
            :extraState="!errorDuplicated.email"
            :validate-immediate="false"
            type="text"
            name="email"
            label="Email"
            @input="onDuplicateInput('email')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.email"
              >The email is already in user by another user, please choose another one</b-form-invalid-feedback
            >
          </app-input>

          <app-input
            v-model="admin.phone"
            rules="required"
            :extraState="!errorDuplicated.phone"
            :validate-immediate="false"
            type="text"
            name="phone"
            label="Phone"
            @input="onDuplicateInput('phone')"
          >
            <b-form-invalid-feedback v-if="errorDuplicated.phone"
              >The phone number is already in use by another user, please choose another one</b-form-invalid-feedback
            >
          </app-input>

          <app-input
            vid="password"
            v-model="admin.password"
            :validate-immediate="false"
            rules="required|password"
            type="password"
            name="password"
            label="Password"
            description="Password must be at least 12 characters long and include 1 uppercase, 1 lowercase, 1 number, and 1 special character."
            autocomplete="new-password"
          ></app-input>

          <app-input
            v-model="admin.confirmed"
            :validate-immediate="false"
            rules="required|password|confirmed:password"
            data-vv-as="password"
            type="password"
            name="confirm"
            label="Confirm Password"
            autocomplete="new-password"
          ></app-input>

          <!-- Admin Type Selector -->
          <app-input
            v-model="tescoAdminType"
            rules="required"
            :validate-immediate="false"
            type="select"
            name="Admin type"
            label="Admin Type"
            :options="adminTypeOptions"
          ></app-input>

          <!-- Conditional sections for "Admin" type -->
          <template v-if="tescoAdminType === 'admin'">
            <!-- Portal Functionality Section -->
            <div class="mt-4">
              <h6>Portal Functionality</h6>
              <p class="text-muted">Select which areas of the portal this admin can access</p>

              <b-form-group>
                <!-- Individual checkboxes for portal functionality -->
                <b-form-checkbox
                  v-for="option in portalFunctionalityOptionsBeforeEmployees"
                  :key="option.value"
                  v-model="portalFunctionality"
                  :value="option.value"
                  class="mb-1"
                  stacked
                >
                  {{ option.text }}
                </b-form-checkbox>

                <!-- Employees checkbox -->
                <b-form-checkbox v-model="portalFunctionality" value="employees" stacked> Employees </b-form-checkbox>

                <!-- Nested Employees Options - appears right after Employees checkbox -->
                <div v-if="portalFunctionality.includes('employees')" class="ml-4 mt-2 mb-3">
                  <!-- Division Checkboxes (only if divisions exist) -->
                  <div v-if="hasDivisions" class="mb-3">
                    <label class="d-block mb-2">Divisions:</label>
                    <app-input
                      v-model="selectedDivisions"
                      type="checkbox"
                      name="divisions"
                      :options="divisionOptions"
                      :disabled="portalFunctionality.includes('divisions')"
                      stacked
                    ></app-input>
                  </div>

                  <!-- Reporting Level Radio Options -->
                  <div>
                    <label class="d-block mb-2">Reporting Level:</label>
                    <app-input
                      v-model="reportingLevel"
                      type="radio"
                      name="reportingLevel"
                      :options="reportingLevelOptions"
                      stacked
                    ></app-input>
                  </div>
                </div>

                <!-- Remaining checkboxes after Employees -->
                <b-form-checkbox
                  v-for="option in portalFunctionalityOptionsAfterEmployees"
                  :key="option.value"
                  v-model="portalFunctionality"
                  :value="option.value"
                  stacked
                  class="mb-1"
                >
                  {{ option.text }}
                </b-form-checkbox>
              </b-form-group>
            </div>

            <!-- Employee Data Attributes Section -->
            <div v-if="shouldShowEmployeeDataAttributes" class="mt-4">
              <h6>Employee Data Attributes</h6>
              <p class="text-muted">
                Select which employee data attributes this admin can view throughout the application
              </p>

              <app-input
                v-model="employeeDataAttributes"
                type="checkbox"
                name="employeeDataAttributes"
                :options="employeeDataAttributesOptions"
                stacked
              ></app-input>
            </div>
          </template>

          <page-sub-footer>
            <template #before>
              <sme-alert v-if="saveError" level="danger">{{ saveError }}</sme-alert>
            </template>
            <b-button type="submit" variant="primary" :disabled="v.invalid || saving">
              <b-spinner v-if="saving" class="mr-2" small />
              {{ saving ? 'Adding...' : 'Add' }}
            </b-button>
            <b-button :to="redirectUrl" variant="outline-primary"> Cancel </b-button>
          </page-sub-footer>
        </b-form>
      </ValidationObserver>
    </template>

    <!-- Original User Form (when tescoPortalStubsEnabled is false) -->
    <template v-else>
      <user-form
        v-model="admin"
        :duplicates="errorDuplicated"
        :error="saveError"
        :cancel-url="redirectUrl"
        :hide-username="shouldHideUsername"
        @save="onSave"
        @duplicate-input="onDuplicateInput"
      ></user-form>
    </template>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import uuidv4 from 'uuid/v4';
import ApiClient from '@/ApiClient';
import AppInput from '@/components/AppInput.vue';
import PageSubFooter from '@/components/PageSubFooter.vue';
import SmeAlert from '@/components/atoms/SmeAlert.vue';
import UserForm from '@/components/UserForm.vue';
import State from '@/state/State';
import useFeatureFlags from '@/composables/useFeatureFlags';
import useDivisionSelection from '@/state/composables/useDivisionSelection';

const defaultAdmin = {
  username: undefined,
  phone: null,
  email: undefined,
  password: undefined,
  confirmed: undefined,
};

export default {
  name: 'AdminAddForm',
  components: { ValidationObserver, AppInput, PageSubFooter, SmeAlert, UserForm },
  props: {
    redirectUrl: {
      type: Object,
      default: () => ({ name: 'admins' }),
    },
    hideUsername: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    const { tescoPortalStubsEnabled, recognitionVersion } = useFeatureFlags();
    const { divisions, hasDivisions } = useDivisionSelection();

    return {
      tescoPortalStubsEnabled,
      recognitionVersion,
      divisions,
      hasDivisions,
    };
  },
  mounted() {
    this.shouldHideUsername = State.state.isSelfServeSME;

    // Initialize Tesco-specific data only when feature is enabled
    if (this.tescoPortalStubsEnabled) {
      // Initialize all divisions as selected by default
      if (this.divisions && this.divisions.length > 0) {
        this.selectedDivisions = this.divisions.map(d => d.division_id);
      }

      // Initialize all portal functionality options as checked
      this.portalFunctionality = this.portalFunctionalityOptions.map(opt => opt.value);

      // Initialize all employee data attributes as checked
      this.employeeDataAttributes = this.employeeDataAttributesOptions.map(opt => opt.value);
    }
  },
  data() {
    return {
      error: undefined,
      saveError: undefined,
      errorDuplicated: {},
      loading: true,
      admin: { ...defaultAdmin },
      saving: false,
      success: false,
      shouldHideUsername: this.hideUsername,
      adminRole: 'company_admin',

      // Tesco-specific fields
      tescoAdminType: undefined,
      portalFunctionality: [],
      selectedDivisions: [],
      reportingLevel: 'all_levels',
      employeeDataAttributes: [],

      // Admin type options
      adminTypeOptions: [
        { value: 'super_admin', text: 'Super admin' },
        { value: 'admin', text: 'Admin' },
        { value: 'admin_manager', text: 'Admin manager' },
      ],

      // Portal functionality options
      portalFunctionalityOptions: [
        { value: 'dashboard', text: 'Dashboard' },
        { value: 'analytics', text: 'Analytics' },
        { value: 'funding_position', text: 'Funding Position' },
        { value: 'divisions', text: 'Divisions' },
        { value: 'employees', text: 'Employees' },
        { value: 'shifts', text: 'Shifts' },
        { value: 'absences', text: 'Absences' },
        { value: 'pay', text: 'Pay' },
        { value: 'admins', text: 'Admins' },
        { value: 'integrations', text: 'Integrations' },
        { value: 'invoices', text: 'Invoices' },
        { value: 'settings', text: 'Settings' },
      ],

      // Reporting level options
      reportingLevelOptions: [
        { value: 'all_levels', text: 'All levels' },
        { value: 'one_level', text: 'One reporting level' },
        { value: 'two_levels', text: 'Two reporting levels' },
      ],
    };
  },
  computed: {
    adminRoleOptions() {
      return [
        { value: 'company_admin', text: 'Full access' },
        { value: 'recognition_admin', text: 'Recognition only' },
      ];
    },
    showAdminRoleSelector() {
      return this.recognitionVersion !== 'off' && !this.tescoPortalStubsEnabled;
    },

    divisionOptions() {
      const divisionsList = this.divisions || [];
      return divisionsList.map(division => ({
        value: division.division_id,
        text: division.division_name || division.name || `Division ${division.division_id}`,
      }));
    },

    portalFunctionalityOptionsBeforeEmployees() {
      return this.portalFunctionalityOptions.filter(opt =>
        ['dashboard', 'analytics', 'funding_position', 'divisions'].includes(opt.value),
      );
    },

    portalFunctionalityOptionsAfterEmployees() {
      return this.portalFunctionalityOptions.filter(opt =>
        ['shifts', 'absences', 'pay', 'admins', 'integrations', 'invoices', 'settings'].includes(opt.value),
      );
    },

    shouldShowEmployeeDataAttributes() {
      const requiredFunctionalities = ['funding_position', 'employees', 'shifts', 'absences', 'divisions'];
      return requiredFunctionalities.some(func => this.portalFunctionality.includes(func));
    },

    employeeDataAttributesOptions() {
      return [
        { value: 'employee_names', text: 'Employee names' },
        { value: 'employee_emails', text: 'Employee emails' },
        { value: 'employee_phones', text: 'Employee phones' },
        { value: 'employee_salary_payment_type', text: 'Employee salary payment type' },
        { value: 'employee_pay_schedule_details', text: 'Employee pay schedule details' },
        {
          value: 'employee_shifts',
          text: 'Employee shifts',
          disabled: this.portalFunctionality.includes('shifts'),
        },
        {
          value: 'employee_absences',
          text: 'Employee absences',
          disabled: this.portalFunctionality.includes('absences'),
        },
        { value: 'employee_pay_usage', text: 'Employee Pay usage' },
        { value: 'employee_status_history', text: 'Employee status history' },
        { value: 'employee_termination_information', text: 'Employee termination information' },
        { value: 'employee_bank_details', text: 'Employee bank details' },
      ];
    },
  },
  watch: {
    portalFunctionality: {
      handler(newValue) {
        // If Divisions is enabled, select all divisions and make them read-only
        const divisionsList = this.divisions || [];
        if (newValue.includes('divisions') && divisionsList.length > 0) {
          this.selectedDivisions = divisionsList.map(d => d.division_id);
        }

        // If Shifts is enabled, ensure employee_shifts is checked
        if (newValue.includes('shifts') && !this.employeeDataAttributes.includes('employee_shifts')) {
          this.employeeDataAttributes.push('employee_shifts');
        }

        // If Absences is enabled, ensure employee_absences is checked
        if (newValue.includes('absences') && !this.employeeDataAttributes.includes('employee_absences')) {
          this.employeeDataAttributes.push('employee_absences');
        }
      },
      deep: true,
    },
  },
  methods: {
    onSave() {
      this.saving = true;
      const newAdmin = {
        ...this.admin,
        user_id: uuidv4(),
      };
      if (!newAdmin.username) {
        newAdmin.username = newAdmin.email;
      }

      if (this.adminRole !== 'company_admin') {
        newAdmin.role = this.adminRole;
      }

      // Add Tesco-specific fields to the admin object if enabled
      if (this.tescoPortalStubsEnabled.value) {
        newAdmin.tesco_admin_type = this.tescoAdminType;

        if (this.tescoAdminType === 'admin') {
          newAdmin.tesco_portal_functionality = this.portalFunctionality;
          newAdmin.tesco_employee_data_attributes = this.employeeDataAttributes;

          if (this.portalFunctionality.includes('employees')) {
            newAdmin.tesco_selected_divisions = this.selectedDivisions;
            newAdmin.tesco_reporting_level = this.reportingLevel;
          }
        }
      }

      ApiClient.addAdmin(State.state.company.company_id, newAdmin).then(
        () => {
          this.$appToast(`${newAdmin.username}'s admin user has been added`, {
            title: 'Successfully added Admin',
            variant: 'success',
          });
          this.$router.push(this.redirectUrl);
        },
        err => {
          this.saving = false;
          this.errorDuplicated = {
            username: false,
            phone: false,
            email: false,
          };
          if (err.message.includes('already taken')) {
            if (err.message.includes('Username')) {
              this.errorDuplicated.username = true;
            }
            if (err.message.includes('users_email_key')) {
              this.errorDuplicated.email = true;
            }
            if (err.message.includes('users_phone_key')) {
              this.errorDuplicated.phone = true;
            }
          } else {
            this.saveError = err.message;
          }
          this.$appToast('Please check the form errors', {
            title: 'Error adding admin',
            variant: 'danger',
          });
        },
      );
    },
    onDuplicateInput(fieldName) {
      this.errorDuplicated[fieldName] = false;
    },
  },
};
</script>

<style scoped>
h4 {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

h5 {
  font-size: 1.1rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
}

/* Add margin to checkbox items in the Employee Data Attributes section */
::v-deep .custom-checkbox {
  margin-bottom: 0.25rem;
}
</style>
